import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Sparkles, Send, TrendingUp, TrendingDown, IndianRupee, Scale, BarChart3, Loader2, Bot, User as UserIcon, Database } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtShort = (n) => {
  if (Math.abs(n) >= 10000000) return '₹' + (n / 10000000).toFixed(2) + ' Cr';
  if (Math.abs(n) >= 100000) return '₹' + (n / 100000).toFixed(2) + ' L';
  if (Math.abs(n) >= 1000) return '₹' + (n / 1000).toFixed(1) + 'K';
  return '₹' + n;
};
const DEFAULT_COLORS = ['#F59E0B', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4'];

export default function FinancialReports() {
  const [tab, setTab] = useState('pnl');
  const [fy, setFy] = useState('');
  const [availableFys, setAvailableFys] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [hasData, setHasData] = useState(true);
  const [loading, setLoading] = useState(false);

  // Claude chat state
  const [chat, setChat] = useState([]); // [{role:'user'|'assistant', text, chart, summary_stats}]
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const chatEndRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const qp = fy ? `?fy=${encodeURIComponent(fy)}` : '';
      const [statsR, pnlR, cfR, bsR, tcR, tvR] = await Promise.all([
        axios.get(`${API}/historical-data/stats`, { withCredentials: true }),
        axios.get(`${API}/financial-reports/pnl${qp}`, { withCredentials: true }),
        axios.get(`${API}/financial-reports/cash-flow${qp}`, { withCredentials: true }),
        axios.get(`${API}/financial-reports/balance-sheet${fy ? `?as_of_fy=${encodeURIComponent(fy)}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/financial-reports/top-parties?kind=customers&limit=10${fy ? `&fy=${encodeURIComponent(fy)}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/financial-reports/top-parties?kind=vendors&limit=10${fy ? `&fy=${encodeURIComponent(fy)}` : ''}`, { withCredentials: true }),
      ]);
      setHasData((statsR.data?.total || 0) > 0);
      setAvailableFys((statsR.data?.fy_buckets || []).map((b) => b.fy));
      setPnl(pnlR.data); setCashFlow(cfR.data); setBalanceSheet(bsR.data);
      setTopCustomers(tcR.data || []); setTopVendors(tvR.data || []);
    } catch (e) { console.error(e); toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [fy]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, asking]);

  const ask = async () => {
    const question = q.trim();
    if (!question || asking) return;
    setChat((c) => [...c, { role: 'user', text: question }]);
    setQ('');
    setAsking(true);
    try {
      const res = await axios.post(`${API}/financial-reports/ask`,
        { question, fy, session_id: sessionId },
        { withCredentials: true });
      setSessionId(res.data.session_id);
      setChat((c) => [...c, {
        role: 'assistant',
        text: res.data.insight,
        chart: res.data.chart,
        summary_stats: res.data.summary_stats,
      }]);
    } catch (e) {
      setChat((c) => [...c, { role: 'assistant', text: `_Error: ${e?.response?.data?.detail || e.message}_` }]);
    } finally { setAsking(false); }
  };

  const exampleQs = [
    'Which month had the highest sales in FY 2024-25?',
    'Top 5 customers by revenue — show a bar chart.',
    'Compare purchases vs sales month over month.',
    'Which R&D trials are loss-making?',
    'Show me the top 5 most profitable formulations.',
    'Break down cash flow by quarter.',
  ];

  return (
    <div className="p-6 space-y-6" data-testid="financial-reports-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2">
            Financial Reports
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30">
              Claude-powered
            </span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">P&amp;L · Cash Flow · Balance Sheet from imported Tally data</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">Financial Year</label>
          <select
            value={fy}
            onChange={(e) => setFy(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-slate-100"
            data-testid="fy-selector"
          >
            <option value="">All Time</option>
            {availableFys.map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
        </div>
      </div>

      {!hasData && (
        <div className="rounded-xl border border-amber-700 bg-amber-900/20 p-6 flex items-start gap-3" data-testid="no-data-banner">
          <Database className="text-amber-400 mt-0.5" />
          <div>
            <p className="text-amber-200 font-medium">No historical data imported yet</p>
            <p className="text-sm text-slate-300 mt-1">
              Go to <Link to="/historical-data" className="underline text-amber-300">Historical Data</Link> and upload your Tally CSV export to unlock these reports.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-800" data-testid="report-tabs">
        {[
          { id: 'pnl', label: 'Profit & Loss', icon: TrendingUp },
          { id: 'cashflow', label: 'Cash Flow', icon: IndianRupee },
          { id: 'balance', label: 'Balance Sheet', icon: Scale },
          { id: 'parties', label: 'Top Parties', icon: BarChart3 },
          { id: 'chat', label: 'Ask Claude', icon: Sparkles },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-amber-500 text-amber-300' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-400 text-sm">Loading…</div>}

      {tab === 'pnl' && pnl && <PnLView data={pnl} />}
      {tab === 'cashflow' && cashFlow && <CashFlowView data={cashFlow} />}
      {tab === 'balance' && balanceSheet && <BalanceSheetView data={balanceSheet} />}
      {tab === 'parties' && <PartiesView customers={topCustomers} vendors={topVendors} />}
      {tab === 'chat' && (
        <ChatView
          chat={chat}
          q={q}
          setQ={setQ}
          ask={ask}
          asking={asking}
          chatEndRef={chatEndRef}
          exampleQs={exampleQs}
          hasData={hasData}
        />
      )}
    </div>
  );
}

// ─── P&L view ──────────────────────────────────────────────────────────────
function PnLView({ data }) {
  return (
    <div className="space-y-6" data-testid="pnl-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Revenue" value={fmt(data.net_revenue)} accent="emerald" />
        <KPI label="Purchases" value={fmt(data.net_purchases)} accent="rose" />
        <KPI label="Gross Profit" value={fmt(data.gross_profit)} sub={`${data.gross_margin_pct}% margin`} accent="amber" />
        <KPI label="Net Profit" value={fmt(data.net_profit)} sub={`${data.net_margin_pct}% margin`} accent={data.net_profit >= 0 ? 'emerald' : 'rose'} />
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-white font-medium mb-3">Monthly Revenue vs Purchases vs Profit</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data.monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
            <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={fmtShort} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
              formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="revenue" fill="#22C55E" name="Revenue" />
            <Bar dataKey="purchases" fill="#F59E0B" name="Purchases" />
            <Bar dataKey="profit" fill="#3B82F6" name="Profit" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <h3 className="text-white font-medium mb-3">Statement of Profit &amp; Loss <span className="text-xs text-slate-400">— {data.fy}</span></h3>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-800">
            <Row label="Revenue from Sales" value={data.revenue} />
            <Row label="Less: Sales Returns" value={-data.sales_returns} dim />
            <Row label="Net Revenue" value={data.net_revenue} bold />
            <Row label="Purchases" value={-data.purchases} />
            <Row label="Less: Purchase Returns" value={data.purchase_returns} dim />
            <Row label="Net Purchases" value={-data.net_purchases} bold />
            <Row label="Gross Profit" value={data.gross_profit} bold highlight />
            <Row label="Other Expenses (Journal)" value={-data.other_expenses} />
            <Row label="Other Income" value={data.other_income} dim />
            <Row label="Net Profit" value={data.net_profit} bold highlight />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Cash Flow view ────────────────────────────────────────────────────────
function CashFlowView({ data }) {
  return (
    <div className="space-y-6" data-testid="cashflow-view">
      <div className="grid grid-cols-3 gap-4">
        <KPI label="Total Inflow" value={fmt(data.total_inflow)} accent="emerald" />
        <KPI label="Total Outflow" value={fmt(data.total_outflow)} accent="rose" />
        <KPI label="Net Cash Flow" value={fmt(data.net_cash_flow)} accent={data.net_cash_flow >= 0 ? 'emerald' : 'rose'} />
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-white font-medium mb-3">Monthly Cash Flow</h3>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data.monthly}>
            <defs>
              <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22C55E" stopOpacity={0.6} /><stop offset="95%" stopColor="#22C55E" stopOpacity={0.05} /></linearGradient>
              <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EF4444" stopOpacity={0.6} /><stop offset="95%" stopColor="#EF4444" stopOpacity={0.05} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
            <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={fmtShort} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
              formatter={(v) => fmt(v)} />
            <Legend />
            <Area type="monotone" dataKey="inflow" stroke="#22C55E" fill="url(#gIn)" name="Inflow" />
            <Area type="monotone" dataKey="outflow" stroke="#EF4444" fill="url(#gOut)" name="Outflow" />
            <Line type="monotone" dataKey="cumulative" stroke="#F59E0B" strokeWidth={2} name="Cumulative" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-xs text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Month</th>
              <th className="text-right px-4 py-2">Inflow</th>
              <th className="text-right px-4 py-2">Outflow</th>
              <th className="text-right px-4 py-2">Net</th>
              <th className="text-right px-4 py-2">Cumulative</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {data.monthly.map((m) => (
              <tr key={m.month} className="hover:bg-slate-800/40">
                <td className="px-4 py-2 text-slate-200">{m.month}</td>
                <td className="px-4 py-2 text-right font-mono text-emerald-300">{fmt(m.inflow)}</td>
                <td className="px-4 py-2 text-right font-mono text-rose-300">{fmt(m.outflow)}</td>
                <td className={`px-4 py-2 text-right font-mono ${m.net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmt(m.net)}</td>
                <td className="px-4 py-2 text-right font-mono text-amber-300">{fmt(m.cumulative)}</td>
              </tr>
            ))}
            {data.monthly.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No receipts/payments in this range</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Balance Sheet view ────────────────────────────────────────────────────
function BalanceSheetView({ data }) {
  return (
    <div className="space-y-6" data-testid="balance-sheet-view">
      <div className="rounded-xl border border-amber-700/40 bg-amber-500/5 p-3 text-xs text-amber-200">
        {data.note}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BalanceSection title="Assets" rows={[
          ['Cash & Bank', data.assets.cash_and_bank],
          ['Debtors (Outstanding)', data.assets.debtors],
        ]} total={data.assets.total} accent="emerald" />
        <BalanceSection title="Liabilities" rows={[
          ['Creditors (Outstanding)', data.liabilities.creditors],
        ]} total={data.liabilities.total} accent="rose" />
        <BalanceSection title="Equity" rows={[
          ['Retained Earnings', data.equity.retained_earnings],
        ]} total={data.equity.total} accent="amber" />
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={[{
            name: 'Balance Sheet',
            Assets: data.assets.total,
            Liabilities: data.liabilities.total,
            Equity: data.equity.total,
          }]}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="name" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" tickFormatter={fmtShort} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
              formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="Assets" fill="#22C55E" />
            <Bar dataKey="Liabilities" fill="#EF4444" />
            <Bar dataKey="Equity" fill="#F59E0B" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BalanceSection({ title, rows, total, accent }) {
  const color = { emerald: 'border-emerald-500/30 text-emerald-300',
    rose: 'border-rose-500/30 text-rose-300',
    amber: 'border-amber-500/30 text-amber-300' }[accent];
  return (
    <div className={`rounded-xl border ${color} bg-slate-900/40 p-5`}>
      <h3 className="text-white font-medium mb-3">{title}</h3>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-800">
          {rows.map(([label, val]) => (
            <tr key={label}>
              <td className="py-2 text-slate-300">{label}</td>
              <td className="py-2 text-right font-mono text-slate-100">{fmt(val)}</td>
            </tr>
          ))}
          <tr className={`border-t-2 ${color}`}>
            <td className="py-2 font-semibold text-white">Total</td>
            <td className={`py-2 text-right font-mono font-semibold ${color.split(' ')[1]}`}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Parties view ──────────────────────────────────────────────────────────
function PartiesView({ customers, vendors }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="parties-view">
      <PartyList title="Top Customers" rows={customers} colorKey="#22C55E" emptyLabel="No sales data" />
      <PartyList title="Top Vendors" rows={vendors} colorKey="#F59E0B" emptyLabel="No purchase data" />
    </div>
  );
}

function PartyList({ title, rows, colorKey, emptyLabel }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center">
        <h3 className="text-white font-medium mb-2">{title}</h3>
        <p className="text-sm text-slate-500">{emptyLabel}</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.amount)) || 1;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
      <h3 className="text-white font-medium mb-3">{title}</h3>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.party}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-slate-200 truncate max-w-[60%]" title={r.party}>{r.party || '—'}</span>
              <span className="font-mono text-slate-100">{fmt(r.amount)}</span>
            </div>
            <div className="h-2 rounded bg-slate-800 overflow-hidden">
              <div className="h-full rounded" style={{ width: `${(r.amount / max * 100)}%`, backgroundColor: colorKey }} />
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{r.count} vouchers</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Chat view ─────────────────────────────────────────────────────────────
function ChatView({ chat, q, setQ, ask, asking, chatEndRef, exampleQs, hasData }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[70vh]" data-testid="chat-view">
      <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-slate-900/40 flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2 text-sm text-slate-300">
          <Sparkles size={14} className="text-purple-400" />
          Chat with Claude Sonnet 4.5 about your financial data
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {chat.length === 0 && (
            <div className="text-center text-slate-500 mt-8">
              <Bot size={32} className="mx-auto mb-3 text-slate-600" />
              <p className="text-sm">Ask a question about your imported data — Claude will explain and draw a chart.</p>
            </div>
          )}
          {chat.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center flex-shrink-0">
                  <Bot size={14} className="text-purple-300" />
                </div>
              )}
              <div className={`max-w-[90%] rounded-xl p-3 text-sm ${
                m.role === 'user' ? 'bg-amber-600/90 text-white' : 'bg-slate-800 text-slate-100'
              }`}>
                {m.role === 'assistant' ? (
                  <>
                    <div className="prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown>{m.text || ''}</ReactMarkdown>
                    </div>
                    {m.chart && m.chart.type !== 'none' && m.chart.data?.length > 0 && (
                      <div className="mt-3 bg-slate-900/60 rounded-lg p-3">
                        <div className="text-xs text-slate-400 mb-2">{m.chart.title}</div>
                        <ClaudeChart chart={m.chart} />
                      </div>
                    )}
                    {m.summary_stats?.length > 0 && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {m.summary_stats.map((s, j) => (
                          <div key={j} className="rounded bg-slate-900/60 border border-slate-700 p-2">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</div>
                            <div className="text-sm font-mono text-amber-300 mt-0.5">{s.value}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <span>{m.text}</span>
                )}
              </div>
              {m.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                  <UserIcon size={14} className="text-amber-300" />
                </div>
              )}
            </div>
          ))}
          {asking && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
                <Bot size={14} className="text-purple-300" />
              </div>
              <div className="bg-slate-800 rounded-xl px-4 py-3 text-sm text-slate-400 flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Claude is thinking…
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="p-3 border-t border-slate-800 flex gap-2">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
            placeholder={hasData ? 'Ask about your financial data…' : 'Upload data first…'}
            disabled={!hasData || asking}
            className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 disabled:opacity-50"
            data-testid="chat-input"
          />
          <button
            onClick={ask}
            disabled={!q.trim() || asking || !hasData}
            className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 text-white rounded-lg text-sm flex items-center gap-2"
            data-testid="chat-send-btn"
          >
            <Send size={14} />Send
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Sparkles size={14} className="text-purple-400" /> Try asking…</h3>
        <div className="space-y-2">
          {exampleQs.map((ex) => (
            <button
              key={ex}
              onClick={() => setQ(ex)}
              disabled={!hasData}
              className="w-full text-left text-sm px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 disabled:opacity-40"
              data-testid="example-question"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClaudeChart({ chart }) {
  const { type, x_key, series, data } = chart;
  const common = { data, margin: { top: 10, right: 10, left: 0, bottom: 0 } };
  if (type === 'pie') {
    const key = series?.[0]?.key || 'value';
    return (
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} dataKey={key} nameKey={x_key} innerRadius={50} outerRadius={90} label>
            {data.map((_, i) => <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => fmt(v)} contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (type === 'line' || type === 'area') {
    const C = type === 'area' ? AreaChart : LineChart;
    return (
      <ResponsiveContainer width="100%" height={260}>
        <C {...common}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey={x_key} stroke="#94a3b8" fontSize={11} />
          <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={fmtShort} />
          <Tooltip formatter={(v) => fmt(v)} contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
          <Legend />
          {series?.map((s, i) => type === 'area'
            ? <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color || DEFAULT_COLORS[i]} fill={s.color || DEFAULT_COLORS[i]} fillOpacity={0.3} />
            : <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color || DEFAULT_COLORS[i]} strokeWidth={2} />)}
        </C>
      </ResponsiveContainer>
    );
  }
  // bar default
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart {...common}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis dataKey={x_key} stroke="#94a3b8" fontSize={11} />
        <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={fmtShort} />
        <Tooltip formatter={(v) => fmt(v)} contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
        <Legend />
        {series?.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color || DEFAULT_COLORS[i]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Shared primitives ─────────────────────────────────────────────────────
function KPI({ label, value, sub, accent }) {
  const colors = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
    rose: 'border-rose-500/30 bg-rose-500/5 text-rose-300',
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[accent]}`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
    </div>
  );
}

function Row({ label, value, bold, highlight, dim }) {
  return (
    <tr className={highlight ? 'bg-amber-500/5' : ''}>
      <td className={`py-2 px-2 ${bold ? 'font-semibold text-white' : dim ? 'text-slate-500 pl-6 text-sm' : 'text-slate-300'}`}>{label}</td>
      <td className={`py-2 px-2 text-right font-mono ${bold ? 'text-white font-semibold' : dim ? 'text-slate-500 text-sm' : value >= 0 ? 'text-slate-100' : 'text-rose-300'}`}>
        {fmt(value)}
      </td>
    </tr>
  );
}
